import { ReactElement, useMemo } from 'react'
import { sortBy, toNumber } from 'lodash-es'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import { useStoreyImages } from '@editor/hooks'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'

interface StoreyImageConfigCopyDialogProps {
  open: boolean
  /** The storey being configured — excluded from the source list. */
  storey: string
  onClose: () => void
  /** Called with the selected source storey's image transform. */
  onSelect: (sourceTransform: StoreyImageTransform) => void
}

/**
 * Dialog listing the other storeys' floorplan images so the current storey can take over the
 * image configuration (scale, offsets, rotation, paper format, transparency) from one of them.
 */
const StoreyImageConfigCopyDialog = ({
  open,
  storey,
  onClose,
  onSelect,
}: StoreyImageConfigCopyDialogProps): ReactElement => {
  const { storeyImages } = useStoreyImages()

  const sourceStoreys = useMemo(
    () =>
      sortBy(
        Object.keys(storeyImages).filter(imageStorey => imageStorey !== storey),
        imageStorey => toNumber(imageStorey),
      ),
    [storeyImages, storey],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Konfiguration von anderem Plan übernehmen</DialogTitle>
      <DialogContent>
        {sourceStoreys.length === 0 ? (
          <Typography>Keine anderen Pläne vorhanden.</Typography>
        ) : (
          <List>
            {sourceStoreys.map(sourceStorey => (
              <ListItemButton
                key={sourceStorey}
                data-cy={`btn-storey-${storey}-image-copy-config-from-${sourceStorey}`}
                onClick={() => {
                  onSelect(storeyImages[sourceStorey])
                  onClose()
                }}
              >
                <ListItemText primary={storeyToLabel(sourceStorey)} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default StoreyImageConfigCopyDialog
